/**
 * ReportPreviewPanel Component (Stories 5.10, 5.11)
 *
 * Inline preview step in the report builder flow.
 * Shows the report canvas in read-only mode with either:
 * - Real integration data (when widgetData prop provided from useReportData)
 * - Mock data (fallback when real data not available)
 *
 * Also displays:
 * - Platform status checklist (real data mode)
 * - Stale comparison badge (when comparison data is stale)
 */

import { useMemo } from "react";
import { ArrowLeft, ArrowsOut, DownloadSimple, FilePdf, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";
import { ReportCanvas } from "./ReportCanvas";
import { PlatformStatusChecklist } from "./PlatformStatusChecklist";
import type { WidgetConfig } from "../../lib/templateUtils";
import type { WidgetData } from "../../lib/widgetData";
import { createLoadingEnvelope } from "../../lib/widgetData";
import { generateMockData } from "../../lib/mockWidgetData";
import { useDebounce } from "../../hooks/useDebounce";
import type { PlatformStatus } from "../../hooks/useReportData";
import type { PdfStatus } from "../../hooks/useGeneratePdf";

export interface DateRangeDisplay {
  start: string;
  end: string;
  comparisonEnabled: boolean;
  comparisonStart: string | null;
  comparisonEnd: string | null;
}

export interface ReportPreviewPanelProps {
  widgets: WidgetConfig[];
  comparisonEnabled: boolean;
  dateRange: DateRangeDisplay;
  clientName?: string;
  onBack: () => void;
  onFullScreen: () => void;
  /** Real widget data from useReportData — when provided, skips mock data generation */
  widgetData?: Record<string, WidgetData>;
  /** Platform status for checklist display */
  platformStatus?: Record<string, PlatformStatus>;
  /** Whether data is still loading from the API */
  dataLoading?: boolean;
  /** Whether comparison data is stale (AC #5) */
  hasStaleComparison?: boolean;
  /** Save report handler — when provided, shows Save Report button */
  onSave?: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Generate PDF handler — when provided, enables Generate PDF button */
  onGeneratePdf?: () => void;
  /** Download PDF handler */
  onDownloadPdf?: () => void;
  /** PDF generation status */
  pdfStatus?: PdfStatus;
  /** Report format (Story 5.23) */
  format?: "document" | "slide-deck";
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function ReportPreviewPanel({
  widgets,
  comparisonEnabled,
  dateRange,
  clientName,
  onBack,
  onFullScreen,
  widgetData: externalWidgetData,
  platformStatus,
  dataLoading,
  hasStaleComparison,
  onSave,
  isSaving,
  onGeneratePdf,
  onDownloadPdf,
  pdfStatus = "idle",
  format = "document",
}: ReportPreviewPanelProps) {
  // Debounce widget changes by 500ms per AC #1
  const debouncedWidgets = useDebounce(widgets, 500);
  const isDebouncing = widgets !== debouncedWidgets;

  // Use real data if provided, otherwise fall back to mock data
  const previewData: Record<string, WidgetData> = useMemo(() => {
    if (externalWidgetData) return externalWidgetData;

    if (isDebouncing) {
      const loadingMap: Record<string, WidgetData> = {};
      for (const widget of widgets) {
        loadingMap[widget.id] = createLoadingEnvelope();
      }
      return loadingMap;
    }
    return generateMockData(debouncedWidgets, comparisonEnabled);
  }, [externalWidgetData, debouncedWidgets, comparisonEnabled, isDebouncing, widgets]);

  const dateDisplay = `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;

  return (
    <div data-testid="report-preview-panel" role="region" aria-label="Report preview" aria-busy={dataLoading}>
      {/* Header bar */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          onClick={onBack}
          data-testid="back-to-layout"
        >
          <ArrowLeft size={16} weight="bold" className="mr-1.5" />
          Back to Layout
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            onClick={onFullScreen}
            data-testid="preview-full-report"
          >
            <ArrowsOut size={16} className="mr-1.5" />
            Preview Full Report
          </Button>
          {onSave && (
            <Button
              size="sm"
              className="min-h-[44px]"
              onClick={onSave}
              disabled={isSaving || dataLoading}
              data-testid="save-report-btn"
            >
              <FloppyDisk size={16} className="mr-1.5" />
              {isSaving ? "Saving..." : "Save Report"}
            </Button>
          )}
          {pdfStatus === "completed" && onDownloadPdf ? (
            <Button
              size="sm"
              className="min-h-[44px]"
              onClick={onDownloadPdf}
              data-testid="download-pdf-btn"
            >
              <DownloadSimple size={16} className="mr-1.5" />
              Download PDF
            </Button>
          ) : (
            <Button
              size="sm"
              className="min-h-[44px]"
              disabled={!onGeneratePdf || pdfStatus === "generating" || dataLoading}
              onClick={onGeneratePdf}
              data-testid="generate-pdf-btn"
            >
              <FilePdf size={16} className="mr-1.5" />
              {pdfStatus === "generating" ? "Generating..." : "Generate PDF"}
            </Button>
          )}
        </div>
      </div>

      {/* Report metadata summary */}
      <div
        className="mb-4 rounded-lg border bg-muted/50 px-4 py-3"
        data-testid="preview-metadata"
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          {clientName && (
            <span>
              <span className="text-muted-foreground">Client:</span>{" "}
              <span className="font-medium">{clientName}</span>
            </span>
          )}
          <span>
            <span className="text-muted-foreground">Date Range:</span>{" "}
            <span className="font-medium">{dateDisplay}</span>
          </span>
          <span>
            <span className="text-muted-foreground">Comparison:</span>{" "}
            <span className="font-medium">
              {comparisonEnabled ? "Enabled" : "Disabled"}
            </span>
          </span>
          <span>
            <span className="text-muted-foreground">Widgets:</span>{" "}
            <span className="font-medium">{widgets.length}</span>
          </span>
        </div>
      </div>

      {/* Platform status checklist — only shown when real data is being fetched */}
      {platformStatus && (
        <PlatformStatusChecklist
          platformStatus={platformStatus}
          isLoading={dataLoading ?? false}
        />
      )}

      {/* Screen reader announcement when all data is loaded */}
      {platformStatus && !dataLoading && Object.keys(platformStatus).length > 0 && (
        <div className="sr-only" aria-live="assertive" data-testid="data-loaded-announcement">
          All data loaded
        </div>
      )}

      {/* Stale comparison badge (AC #5) */}
      {hasStaleComparison && comparisonEnabled && (
        <div
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700"
          role="status"
          data-testid="stale-comparison-badge"
        >
          Comparison data may be outdated. Some platforms returned cached data from a previous period.
        </div>
      )}

      {/* Read-only canvas preview */}
      <ReportCanvas
        widgets={widgets}
        widgetData={previewData}
        editable={false}
        format={format}
      />
    </div>
  );
}
